/**
 * ContactsListPage — table view over vault_business_contacts with the
 * Leads-private extension fields (lead_score / last_contacted_at)
 * surfaced alongside. Filters: search + lifecycle stage + company.
 *
 * Rows are multi-selectable for bulk archive — the cleanup path for
 * bad data that predates import-batch tracking. The Archived view
 * lists archived contacts and bulk-restores them, or permanently
 * deletes them behind a type-to-confirm dialog (/bulk/contacts
 * endpoints). Columns sort client-side via lib/table-sort.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Copy } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsContacts,
  useFiasLeadsCompanyRefs,
  useCreateFiasLeadsContact,
  useFiasLeadsCrossArchePresence,
  useBulkArchiveFiasLeadsContacts,
  useBulkRestoreFiasLeadsContacts,
  useBulkDeleteFiasLeadsContacts,
} from '../hooks/useFiasLeads';
import { useListSelection } from '../hooks/useListSelection';
import { useToast } from '@/components/ui/use-toast';
import { SkeletonRow } from '../components/Skeletons';
import { BulkArchiveBar } from '../components/BulkArchiveBar';
import { BulkDeleteDialog } from '../components/BulkDeleteDialog';
import { CrossArcheBadge } from '../components/CrossArcheBadge';
import { DedupDialog } from '../components/DedupDialog';
import { SortableTh, sortRows, useTableSort, type SortValue } from '../lib/table-sort';
import { summarizeBulkDelete } from '../lib/bulk-delete-failures';

type ContactSortKey = 'name' | 'email' | 'title' | 'stage' | 'score' | 'company';

interface Props {
  holderId: string;
}

export function ContactsListPage({ holderId }: Props) {
  const [companyId, setCompanyId] = useState<string>('');
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDedup, setShowDedup] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ firstName: '', lastName: '', primaryEmail: '' });
  const { toast } = useToast();

  // 500 is the server-side cap on this list. Fetch the maximum so bulk
  // cleanup can chew through big messes in as few passes as possible.
  const contactsQuery = useFiasLeadsContacts(holderId, {
    companyId: companyId || undefined,
    includeArchived: showArchived || undefined,
    limit: 500,
  });
  // Refs, not the full company list: complete (no 100-row default cap)
  // and alphabetized server-side — feeds the filter dropdown AND the
  // Company column's id → name map.
  const companyRefsQuery = useFiasLeadsCompanyRefs(holderId);
  const companyNameById = useMemo(
    () => new Map((companyRefsQuery.data ?? []).map((r) => [r.companyId, r.name])),
    [companyRefsQuery.data],
  );
  const createContact = useCreateFiasLeadsContact(holderId);
  const presenceQuery = useFiasLeadsCrossArchePresence(
    holderId,
    (contactsQuery.data ?? []).map((c) => c.contactId),
  );
  const contactPresence = presenceQuery.data?.contactPresence ?? {};

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.firstName.trim()) return;
    await createContact.mutateAsync({
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim() || undefined,
      primaryEmail: draft.primaryEmail.trim() || undefined,
      companyId: companyId || undefined,
    });
    setDraft({ firstName: '', lastName: '', primaryEmail: '' });
    setShowCreate(false);
  };

  const filtered = useMemo(() => {
    const all = contactsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (showArchived ? !c.archivedAt : !!c.archivedAt) return false;
      if (lifecycleFilter && c.lifecycleStage !== lifecycleFilter) return false;
      if (!q) return true;
      const matches =
        c.firstName.toLowerCase().includes(q) ||
        (c.lastName ?? '').toLowerCase().includes(q) ||
        (c.primaryEmail ?? '').toLowerCase().includes(q) ||
        (c.title ?? '').toLowerCase().includes(q);
      return matches;
    });
  }, [contactsQuery.data, search, lifecycleFilter, showArchived]);

  const stagesSet = new Set<string>();
  for (const c of contactsQuery.data ?? []) {
    if (c.lifecycleStage) stagesSet.add(c.lifecycleStage);
  }
  const stages = [...stagesSet].sort();

  // Display name for the Company column: the linked company's name
  // wins; the raw imported text is the fallback both while unlinked
  // AND when the linked company is archived (refs exclude archived, so
  // the map misses it — the provenance text is better than a blank).
  const companyDisplay = (c: (typeof filtered)[number]): string | null =>
    (c.companyId ? (companyNameById.get(c.companyId) ?? c.companyName) : c.companyName) ?? null;

  const sort = useTableSort<ContactSortKey>();
  const sorted = useMemo(() => {
    if (!sort.sortKey) return filtered; // server order (updated_at DESC)
    const key = sort.sortKey;
    const accessor = (c: (typeof filtered)[number]): SortValue => {
      switch (key) {
        case 'name':
          return `${c.firstName} ${c.lastName ?? ''}`.trim();
        case 'email':
          return c.primaryEmail;
        case 'title':
          return c.title;
        case 'stage':
          return c.lifecycleStage;
        case 'score':
          return c.extension?.leadScore ?? null;
        case 'company':
          return companyDisplay(c);
      }
    };
    return sortRows(filtered, accessor, sort.direction);
    // companyNameById is included because companyDisplay reads it for
    // the 'company' key.
  }, [filtered, sort.sortKey, sort.direction, companyNameById]);

  const selection = useListSelection(sorted.map((c) => c.contactId));
  const bulkArchive = useBulkArchiveFiasLeadsContacts(holderId);
  const bulkRestore = useBulkRestoreFiasLeadsContacts(holderId);
  const bulkDelete = useBulkDeleteFiasLeadsContacts(holderId);

  const handleBulk = async () => {
    const mutation = showArchived ? bulkRestore : bulkArchive;
    const verb = showArchived ? 'restored' : 'archived';
    const ids = selection.selected;
    try {
      const { results } = await mutation.mutateAsync(ids);
      const failed = results.filter((r) => !r.ok).length;
      selection.clear();
      toast({
        title: `${results.length - failed} contact${results.length - failed === 1 ? '' : 's'} ${verb}`,
        ...(failed > 0
          ? { description: `${failed} could not be ${verb}.`, variant: 'destructive' as const }
          : {}),
      });
    } catch (err) {
      toast({
        title: `Could not ${showArchived ? 'restore' : 'archive'} — try again`,
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleBulkDelete = async () => {
    try {
      const { results } = await bulkDelete.mutateAsync(selection.selected);
      const summary = summarizeBulkDelete(results);
      selection.clear();
      setDeleteDialogOpen(false);
      toast({
        title: `${summary.deleted} contact${summary.deleted === 1 ? '' : 's'} permanently deleted`,
        ...(summary.failed > 0
          ? { description: summary.failureDescription, variant: 'destructive' as const }
          : {}),
      });
    } catch (err) {
      toast({
        title: 'Could not delete — try again',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Contacts</h1>
          <p className="text-sm text-muted-foreground">
            People you sell to. Shared with Fias 360 in this workspace.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowArchived((v) => !v);
              selection.clear();
            }}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
          >
            {showArchived ? 'Back to contacts' : 'Show archived'}
          </button>
          <button
            onClick={() => setShowDedup(true)}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
            title="Find duplicate contacts"
          >
            <Copy className="h-4 w-4" />
            Dedup
          </button>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
          >
            <Plus className="h-4 w-4" />
            New contact
          </button>
        </div>
      </header>

      {showDedup && <DedupDialog holderId={holderId} onClose={() => setShowDedup(false)} />}

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="flex flex-wrap gap-2 rounded border border-border bg-card p-4"
        >
          <input
            type="text"
            placeholder="First name *"
            value={draft.firstName}
            onChange={(e) => setDraft((p) => ({ ...p, firstName: e.target.value }))}
            className="flex-1 min-w-[150px] rounded border border-border bg-background px-3 py-2"
            required
            autoFocus
          />
          <input
            type="text"
            placeholder="Last name"
            value={draft.lastName}
            onChange={(e) => setDraft((p) => ({ ...p, lastName: e.target.value }))}
            className="flex-1 min-w-[150px] rounded border border-border bg-background px-3 py-2"
          />
          <input
            type="email"
            placeholder="Email"
            value={draft.primaryEmail}
            onChange={(e) => setDraft((p) => ({ ...p, primaryEmail: e.target.value }))}
            className="flex-1 min-w-[200px] rounded border border-border bg-background px-3 py-2"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.firstName.trim() || createContact.isPending}
              className="rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
            >
              {createContact.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search contacts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded border border-border bg-card pl-10 pr-3 py-2 text-sm"
          />
        </div>
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All companies</option>
          {(companyRefsQuery.data ?? []).map((c) => (
            <option key={c.companyId} value={c.companyId}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All stages</option>
          {stages.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <BulkArchiveBar
        mode={showArchived ? 'restore' : 'archive'}
        selectedCount={selection.selected.length}
        noun="contact"
        nounPlural="contacts"
        pending={bulkArchive.isPending || bulkRestore.isPending}
        onConfirm={() => void handleBulk()}
        onClear={selection.clear}
        onDelete={showArchived ? () => setDeleteDialogOpen(true) : undefined}
        deletePending={bulkDelete.isPending}
      />

      <BulkDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        count={selection.selected.length}
        noun="contact"
        nounPlural="contacts"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDelete.isPending}
      />

      {showArchived && (
        <p className="text-sm text-muted-foreground">
          Archived contacts — select any to restore them, or permanently delete them.
        </p>
      )}

      {contactsQuery.isLoading && <SkeletonRow />}
      {contactsQuery.isError && (
        <p className="text-destructive text-sm">
          Failed to load contacts: {(contactsQuery.error as Error).message}
        </p>
      )}

      <div className="rounded border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40">
            <tr>
              <th className="w-10 px-4 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all contacts"
                  checked={selection.allSelected}
                  onChange={selection.toggleAll}
                />
              </th>
              <SortableTh
                label="Name"
                active={sort.sortKey === 'name'}
                direction={sort.direction}
                onClick={() => sort.toggle('name')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Email"
                active={sort.sortKey === 'email'}
                direction={sort.direction}
                onClick={() => sort.toggle('email')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Title"
                active={sort.sortKey === 'title'}
                direction={sort.direction}
                onClick={() => sort.toggle('title')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Company"
                active={sort.sortKey === 'company'}
                direction={sort.direction}
                onClick={() => sort.toggle('company')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Stage"
                active={sort.sortKey === 'stage'}
                direction={sort.direction}
                onClick={() => sort.toggle('stage')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Lead score"
                active={sort.sortKey === 'score'}
                direction={sort.direction}
                onClick={() => sort.toggle('score')}
                className="text-left px-4 py-2 font-medium"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((c) => (
              <tr key={c.contactId} className="hover:bg-muted/30">
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${c.firstName} ${c.lastName ?? ''}`.trim()}
                    checked={selection.isSelected(c.contactId)}
                    onChange={() => selection.toggle(c.contactId)}
                  />
                </td>
                <td className="px-4 py-2">
                  <Link
                    to={`${ARCHE_PATH}/contacts/${c.contactId}`}
                    className="text-foreground hover:underline"
                  >
                    {c.firstName} {c.lastName ?? ''}
                  </Link>
                  <CrossArcheBadge present={contactPresence[c.contactId] === true} />
                </td>
                <td className="px-4 py-2 text-muted-foreground">{c.primaryEmail ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground">{c.title ?? '—'}</td>
                <td className="px-4 py-2">
                  {c.companyId ? (
                    <Link
                      to={`${ARCHE_PATH}/companies/${c.companyId}`}
                      className="text-foreground hover:underline"
                      title={
                        companyNameById.get(c.companyId) ? undefined : 'Linked company is archived'
                      }
                    >
                      {companyNameById.get(c.companyId) ?? c.companyName ?? 'Archived company'}
                    </Link>
                  ) : c.companyName ? (
                    <span
                      className="text-muted-foreground"
                      title="Imported name — no matching company yet"
                    >
                      {c.companyName}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-muted-foreground text-xs">
                  {c.lifecycleStage ?? '—'}
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {c.extension?.leadScore != null ? c.extension.leadScore.toFixed(0) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(contactsQuery.data?.length ?? 0) >= 500 && (
          <p className="border-t border-border p-3 text-center text-xs text-muted-foreground">
            Showing the first 500 contacts. Filter to narrow down, or archive some — the next 500
            load as the list empties.
          </p>
        )}
        {filtered.length === 0 && !contactsQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            {search.trim() || companyId || lifecycleFilter
              ? 'No contacts match these filters.'
              : 'No contacts yet. Add your first one above.'}
          </p>
        )}
      </div>
    </div>
  );
}
